// src/pages/FleetSelector.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAccounts, getFleetToken } from '../api';
import {
  Container,
  TextField,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';

const FleetSelector = () => {
  const [fleets, setFleets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    // Store the organization token
    sessionStorage.setItem('orgToken', token);

    const fetchFleets = async () => {
      try {
        setLoading(true);
        const accounts = await getAccounts(token);
        // Filter out organizations (type 1)
        const filteredFleets = accounts.filter(account => account.type === 1);
        setFleets(filteredFleets);
      } catch (err) {
        setError(err.message || 'Failed to fetch fleets');
      } finally {
        setLoading(false);
      }
    };

    fetchFleets();
  }, [navigate]);

  const handleFleetClick = async (fleet) => {
    const orgToken = sessionStorage.getItem('orgToken');
    if (!orgToken) {
      navigate('/');
      return;
    }

    try {
      setLoading(true);
      const fleetToken = await getFleetToken(orgToken, fleet.uid);
      sessionStorage.setItem('fleetToken', fleetToken);
      sessionStorage.setItem('selectedFleetName', fleet.name);
      navigate(`/fleets/${fleet.uid}/drivers`);
    } catch (err) {
      setError(err.message || 'Failed to get fleet token');
    } finally {
      setLoading(false);
    }
  };

  const filteredFleets = fleets.filter(fleet =>
    fleet.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    fleet.uid.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container component="main" maxWidth="sm">
      <Paper elevation={3} sx={{ mt: 2, p: 2 }}>
        <Typography component="h1" variant="h5" gutterBottom>
          Select a Fleet
        </Typography>
        <TextField
          fullWidth
          size="small"
          label="Search Fleets"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 2 }}
        />
        {error && (
          <Typography color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <List>
            {filteredFleets.map((fleet) => (
              <ListItem
                key={fleet.uid}
                button
                onClick={() => handleFleetClick(fleet)}
                sx={{
                  py: 0.5,
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      {fleet.name}
                    </Typography>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      ID: {fleet.uid}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
    </Container>
  );
};

export default FleetSelector; 